import * as React from "react"
import { OTPInput, OTPInputContext } from "input-otp"
import { Dot } from "lucide-react"

import { cn } from "@/lib/utils"

const InputOTP = React.forwardRef<
  React.ElementRef<typeof OTPInput>,
  React.ComponentPropsWithoutRef<typeof OTPInput>
>(({ className, containerClassName, ...props }, ref) => (
  <OTPInput
    ref={ref}
    containerClassName={cn(
      "flex items-center gap-2 has-[:disabled]:opacity-50",
      containerClassName
    )}
    className={cn("disabled:cursor-not-allowed", className)}
    {...props}
  />
))
InputOTP.displayName = "InputOTP"

const InputOTPGroup = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center", className)} {...props} />
))
InputOTPGroup.displayName = "InputOTPGroup"

const InputOTPSlot = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div"> & { index: number }
>(({ index, className, ...props }, ref) => {
  const inputOTPContext = React.useContext(OTPInputContext)
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index]

  return (
    <div
      ref={ref}
      className={cn(
        // Base: slot individual com cantos arredondados e fundo branco
        "relative flex h-14 w-12 items-center justify-center rounded-2xl border border-[#e0e5f2] bg-white text-[22px] font-semibold text-[#2b3674] transition-all duration-200 select-none cursor-text",
        // Slot ativo/focado: borda roxa + glow suave + leve escala
        isActive
          ? "border-[#4318ff] ring-2 ring-[#4318ff]/20 shadow-[0_0_0_4px_rgba(67,24,255,0.08)] scale-105 z-10"
          : "hover:border-[#a3aed0]",
        // Slot preenchido (sem foco): leve highlight lilás
        char && !isActive && "border-[#4318ff]/40 bg-[#f5f3ff]/60",
        className
      )}
      {...props}
    >
      {/* Caractere com animação de pop ao aparecer */}
      <span
        style={{
          display: "inline-block",
          transform: char ? "scale(1)" : "scale(0.4)",
          opacity: char ? 1 : 0,
          transition: "transform 0.15s cubic-bezier(0.34,1.56,0.64,1), opacity 0.1s ease",
        }}
      >
        {char}
      </span>

      {/* Cursor piscante premium */}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div
            className="h-6 w-[2px] rounded-full bg-[#4318ff] animate-caret-blink"
            style={{ animationDuration: "1s" }}
          />
        </div>
      )}
    </div>
  )
})
InputOTPSlot.displayName = "InputOTPSlot"

const InputOTPSeparator = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div">
>(({ ...props }, ref) => (
  <div ref={ref} role="separator" {...props}>
    <Dot />
  </div>
))
InputOTPSeparator.displayName = "InputOTPSeparator"

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator }
